import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

import { useAuth } from '../../contexts/AuthContext';
import { getApiBaseUrl } from '../../utils/backendBaseUrl';
import { appPath } from '../../utils/appRouting';

const API = getApiBaseUrl();

function KindButton({ active, onClick, title, subtitle }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-active={active}
      className={
        'text-left rounded-2xl border px-4 py-3 transition-colors ' +
        (active ? 'bg-accent/50 border-border' : 'bg-background/40 border-border/60 hover:bg-accent/30')
      }
    >
      <div className="text-sm font-semibold text-foreground">{title}</div>
      <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>
    </button>
  );
}

export default function CommunityComposeDialog({ open, onOpenChange, initialKind, onNavigate }) {
  const { currentUser } = useAuth();

  const [kind, setKind] = useState(initialKind || 'discussion');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [question, setQuestion] = useState('');
  const [optA, setOptA] = useState('');
  const [optB, setOptB] = useState('');
  const [tags, setTags] = useState('');
  const [sending, setSending] = useState(false);

  const isPoll = String(kind) === 'poll';

  const canSubmit = useMemo(() => {
    if (!String(title || '').trim() || String(title).trim().length < 3) return false;
    if (isPoll) {
      if (!String(question || '').trim()) return false;
      const a = String(optA || '').trim();
      const b = String(optB || '').trim();
      return Boolean(a && b);
    }
    return Boolean(String(body || '').trim().length >= 3);
  }, [title, body, isPoll, question, optA, optB]);

  const reset = () => {
    setTitle('');
    setBody('');
    setQuestion('');
    setOptA('');
    setOptB('');
    setTags('');
  };

  const handleClose = (nextOpen) => {
    if (!nextOpen) {
      reset();
      setKind(initialKind || 'discussion');
    }
    onOpenChange?.(nextOpen);
  };

  const submit = async () => {
    if (!currentUser) {
      toast.error('Devi essere autenticato');
      return;
    }
    if (!canSubmit) return;

    setSending(true);
    try {
      const token = await currentUser.getIdToken();
      const payload = {
        post_kind: kind,
        title: String(title || '').trim(),
        body: !isPoll ? String(body || '').trim() : undefined,
        question: isPoll ? String(question || '').trim() : undefined,
        options: isPoll ? [String(optA || '').trim(), String(optB || '').trim()] : undefined,
        tags: String(tags || '').trim() || undefined,
      };

      const res = await axios.post(`${API}/community/posts`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 12000,
      });

      const url = res?.data?.url;
      toast.success('Pubblicato in community');
      handleClose(false);

      // Naviga al contenuto appena creato (o al feed)
      if (url && typeof onNavigate === 'function') {
        onNavigate(appPath(url));
      } else if (typeof onNavigate === 'function') {
        onNavigate(appPath('/community'));
      }
    } catch (e) {
      console.error('Community post error', e);
      toast.error(e?.response?.data?.detail || 'Errore nella pubblicazione');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl rounded-3xl">
        <DialogHeader>
          <DialogTitle>Condividi in Community</DialogTitle>
          <DialogDescription>
            Linguaggio professionale e sobrio. Nessun marketplace: solo condivisione, discussione e riuso.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <KindButton
            active={kind === 'discussion'}
            onClick={() => setKind('discussion')}
            title="Discussione"
            subtitle="Domanda concreta, confronto rapido"
          />
          <KindButton
            active={kind === 'experience'}
            onClick={() => setKind('experience')}
            title="Esperienza"
            subtitle="Cosa hai provato in classe e com’è andata"
          />
          <KindButton
            active={kind === 'poll'}
            onClick={() => setKind('poll')}
            title="Sondaggio"
            subtitle="Due opzioni rapide, utile per decisioni"
          />
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3">
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1">Titolo</div>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Esempio: Strategie per attenzione in 2ª primaria"
              className="rounded-2xl"
            />
          </div>

          {!isPoll ? (
            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-1">Testo</div>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Scrivi in modo pratico: contesto, obiettivo, cosa hai fatto, cosa ha funzionato."
                className="rounded-2xl min-h-[140px]"
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              <div>
                <div className="text-xs font-semibold text-muted-foreground mb-1">Domanda</div>
                <Input
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Esempio: Meglio routine visiva o token economy?"
                  className="rounded-2xl"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <div className="text-xs font-semibold text-muted-foreground mb-1">Opzione A</div>
                  <Input value={optA} onChange={(e) => setOptA(e.target.value)} className="rounded-2xl" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-muted-foreground mb-1">Opzione B</div>
                  <Input value={optB} onChange={(e) => setOptB(e.target.value)} className="rounded-2xl" />
                </div>
              </div>
            </div>
          )}

          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1">Tag (opzionale)</div>
            <Input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="Esempio: primaria, autonomia, ADHD"
              className="rounded-2xl"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:justify-between pt-2">
            <Button
              type="button"
              variant="outline"
              className="rounded-2xl"
              onClick={() => {
                handleClose(false);
                onNavigate?.(appPath('/libreria-materiali'));
              }}
            >
              Condividi un materiale…
            </Button>

            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" className="rounded-2xl" onClick={() => handleClose(false)}>
                Annulla
              </Button>
              <Button type="button" className="rounded-2xl" disabled={!canSubmit || sending} onClick={submit}>
                {sending ? 'Pubblico…' : 'Pubblica'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
